// Installer notes (Automated Modules) may be plain text or a small HTML fragment. HTML is
// rendered through a tag/attribute allowlist so a note can format a procedure (headings, steps,
// bold warnings) but never run script, load content or restyle the page.

const ALLOWED_TAGS = new Set([
    'p', 'br', 'hr', 'div', 'span', 'section',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'b', 'strong', 'i', 'em', 'u', 's', 'small', 'mark', 'sub', 'sup', 'code', 'pre', 'blockquote',
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a',
]);

// Dropped together with everything inside them.
const DROPPED_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'svg', 'math', 'head', 'title', 'link', 'meta']);

/** True when the note carries markup (any element tag), not just text. */
export function isHtmlNote(note) {
    return !!note && /<\/?[a-z][a-z0-9]*(\s[^>]*)?\/?>/i.test(note);
}

function cleanNode(node, doc) {
    if (node.nodeType === Node.TEXT_NODE) return doc.createTextNode(node.textContent ?? '');
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node;
    const tag = el.tagName.toLowerCase();
    if (DROPPED_TAGS.has(tag)) return null;

    const children = Array.from(el.childNodes).map((child) => cleanNode(child, doc)).filter(Boolean);
    if (!ALLOWED_TAGS.has(tag)) {
        // Unknown wrapper (font, center, …): keep its content, lose the tag.
        const fragment = doc.createDocumentFragment();
        children.forEach((child) => fragment.appendChild(child));
        return fragment;
    }

    const out = doc.createElement(tag);
    if (tag === 'a') {
        const href = (el.getAttribute('href') ?? '').trim();
        if (/^(https?:|mailto:)/i.test(href)) {
            out.setAttribute('href', href);
            out.setAttribute('target', '_blank');
            out.setAttribute('rel', 'noopener noreferrer');
        }
    }
    if ((tag === 'td' || tag === 'th') && el.getAttribute('colspan')) {
        const span = Number.parseInt(el.getAttribute('colspan') ?? '', 10);
        if (span > 1 && span < 50) out.setAttribute('colspan', String(span));
    }
    children.forEach((child) => out.appendChild(child));
    return out;
}

/** The note's HTML reduced to the allowlist: no attributes beyond safe links, no scripts or styles. */
export function sanitizeNoteHtml(note) {
    const parsed = new DOMParser().parseFromString(note, 'text/html');
    const container = document.createElement('div');
    Array.from(parsed.body.childNodes).forEach((child) => {
        const clean = cleanNode(child, document);
        if (clean) container.appendChild(clean);
    });
    return container.innerHTML;
}

/** Plain text of a note (tags stripped, whitespace collapsed) — for tooltips and one-line previews. */
export function noteToPlainText(note) {
    if (!note) return '';
    if (!isHtmlNote(note)) return note.replace(/\s+/g, ' ').trim();
    const withBreaks = note.replace(/<(br|\/p|\/li|\/h[1-6]|\/div|\/tr)\s*\/?>/gi, '$& ');
    const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
    return text.replace(/\s+/g, ' ').trim();
}
